/**
 * useSessionUpdates — Reactive polling hook for session background processing updates.
 *
 * Observes session status and updated_at from Redux:
 * - When status is a processing status → start polling + full refresh
 * - When updated_at changes (from any source) → start polling for 30s cooldown
 * - When processing → idle transition → keep polling for 30s cooldown
 * - If status goes back to processing during cooldown → cycle restarts
 * - After 30s of no changes → stop polling
 *
 * Backend confirmed: updated_at is updated on every session change (status, fields, notes, etc.)
 *
 * Also watches reanalysisStatus from note save to kick off a status fetch.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchSession, fetchSessionStatusThunk, setPolling } from '../store/sessionSlice';
import { fetchTranscript } from '../store/transcriptSlice';

// Polling interval for lightweight status checks
const POLL_INTERVAL_MS = 3_000;

// Keep polling for 30s after last change when idle
const COOLDOWN_MS = 30_000;

export function useSessionUpdates(
  sessionId: string | undefined,
  options?: { paused?: boolean },
): { isPolling: boolean } {
  const paused = options?.paused ?? false;
  const dispatch = useAppDispatch();
  const [isPolling, setIsPolling] = useState(false);

  // Read from Redux
  const runningTasks = useAppSelector((s) => s.session.currentSession?.running_tasks);
  const sessionUpdatedAt = useAppSelector((s) => s.session.currentSession?.updated_at);
  const reanalysisStatus = useAppSelector((s) => s.session.reanalysisStatus);

  // Derived — processing if any background tasks are running
  const isProcessing = (runningTasks?.length ?? 0) > 0;

  // Refs for polling loop state
  const isPollingRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastUpdatedAtRef = useRef<string | null>(null);
  const prevUpdatedAtRef = useRef<string | undefined>(undefined); // Track Redux value for change detection

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    setIsPolling(false);
    clearTimeout(pollTimeoutRef.current);
    clearTimeout(cooldownTimeoutRef.current);
  }, []);

  const doFullRefresh = useCallback((sid: string) => {
    dispatch(fetchSession(sid));
    dispatch(fetchTranscript(sid));
  }, [dispatch]);

  // Polling loop: fetch status every 3s, detect updated_at changes → full refresh
  const startPolling = useCallback(() => {
    if (isPollingRef.current || !sessionId) return;
    isPollingRef.current = true;
    setIsPolling(true);

    const poll = async () => {
      if (!isPollingRef.current || !sessionId) return;

      try {
        const result = await dispatch(fetchSessionStatusThunk(sessionId)).unwrap();

        // If updated_at changed since last poll, do a full refresh
        if (lastUpdatedAtRef.current !== null && result.updated_at !== lastUpdatedAtRef.current) {
          doFullRefresh(sessionId);
        }
        lastUpdatedAtRef.current = result.updated_at;
      } catch {
        // Ignore — will retry next interval
      }

      if (isPollingRef.current) {
        pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
  }, [sessionId, dispatch, doFullRefresh]);

  // When sessionId changes (e.g., navigating to /session/new), immediately stop
  // any in-progress polling from the previous session. Without this, stale
  // fetchSession calls from the old polling loop can resolve after
  // clearCurrentSession() and repopulate currentSession with old data, causing
  // the "bounce back" redirect bug.
  useEffect(() => {
    if (!sessionId) {
      stopPolling();
      lastUpdatedAtRef.current = null;
      prevUpdatedAtRef.current = undefined;
      return;
    }

    // Paused (e.g., the "Add More Conversation" resume screen is open) — halt
    // any in-flight polling so a background refresh doesn't disrupt the screen.
    if (paused) {
      stopPolling();
      return;
    }

    if (isProcessing) {
      // Processing → clear cooldown, ensure polling, full refresh
      clearTimeout(cooldownTimeoutRef.current);
      doFullRefresh(sessionId);
      startPolling();
    } else if (isPollingRef.current) {
      // Already polling but idle → (re)set 30s cooldown
      clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = setTimeout(() => {
        doFullRefresh(sessionId);
        stopPolling();
      }, COOLDOWN_MS);
    } else if (
      prevUpdatedAtRef.current !== undefined &&
      sessionUpdatedAt !== prevUpdatedAtRef.current
    ) {
      // Not polling, not processing, but updated_at just CHANGED →
      // Something happened (e.g., recording stopped, backend started processing).
      // Start polling for 30s to catch any upcoming processing transitions.
      startPolling();
      cooldownTimeoutRef.current = setTimeout(() => {
        doFullRefresh(sessionId);
        stopPolling();
      }, COOLDOWN_MS);
    }

    // Track previous value for change detection
    prevUpdatedAtRef.current = sessionUpdatedAt;
  }, [sessionId, isProcessing, sessionUpdatedAt, paused, startPolling, stopPolling, doFullRefresh]);

  // When note save triggers reanalysis, kick off a status fetch after a short delay
  // so we pick up the new processing status (which triggers the main effect)
  useEffect(() => {
    if (!sessionId || reanalysisStatus !== 'pending') return;

    const timer = setTimeout(() => {
      dispatch(fetchSessionStatusThunk(sessionId));
    }, 1_000);

    return () => clearTimeout(timer);
  }, [sessionId, reanalysisStatus, dispatch]);

  // Mirror polling state into Redux so navigation handlers (Header tabs, New
  // Session button, logo) can detect an ongoing pull and fall back to a hard
  // page load instead of a client-side navigation.
  useEffect(() => {
    dispatch(setPolling(isPolling));
  }, [isPolling, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isPollingRef.current = false;
      clearTimeout(pollTimeoutRef.current);
      clearTimeout(cooldownTimeoutRef.current);
      dispatch(setPolling(false));
    };
  }, [dispatch]);

  return { isPolling };
}
